/**
 * Helpers de consulta do modulo financeiro (task #18, Fase 1-B).
 *
 * Dois problemas se repetem em todos os hooks do modulo e vivem aqui:
 *
 * 1. DEGRADACAO ELEGANTE — enquanto as migrations 144/145/146 nao forem
 *    aplicadas, as tabelas/views `credit_v2_fin_*` nao existem e o PostgREST
 *    responde "relation does not exist" / "could not find the table in the
 *    schema cache". Isso vira `notReady`, nunca tela branca ou toast em loop.
 *
 * 2. TETO DE LINHAS — o PostgREST do CreditOS corta em 1000 linhas por
 *    resposta (`db-max-rows`). Pedir `.limit(5000)` NAO resolve: o servidor
 *    ignora e devolve 1000, silenciosamente. A unica saida correta e paginar
 *    com `.range()` ate a pagina vir incompleta — mesma tecnica do
 *    `fetchAllPages` do app do Michel.
 */

/** Codigos que o PostgREST/Postgres usam quando o objeto simplesmente nao existe. */
const CODIGOS_OBJETO_AUSENTE = new Set(["42P01", "PGRST002", "PGRST200", "PGRST202", "PGRST205"])

/** True quando o erro significa "o modulo financeiro ainda nao foi instalado neste banco". */
export function isModuloNaoInstalado(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const e = error as { code?: string; message?: string; details?: string; hint?: string }
  if (e.code && CODIGOS_OBJETO_AUSENTE.has(e.code)) return true
  const texto = `${e.message ?? ""} ${e.details ?? ""} ${e.hint ?? ""}`.toLowerCase()
  return (
    texto.includes("does not exist") ||
    texto.includes("schema cache") ||
    texto.includes("could not find the table")
  )
}

/**
 * True quando o erro diz que uma destas COLUNAS nao existe no banco — a
 * migration que as cria ainda nao chegou.
 *
 * 🚨 TEM DE SER PERGUNTADO ANTES DE `isModuloNaoInstalado`. Aquele le qualquer
 * "does not exist" como "o modulo nao esta instalado" e desliga o Financeiro
 * inteiro (`notReady`). Coluna nova que falta nao e motivo para isso: a leitura
 * pode voltar as colunas antigas. Conferido no ensaio em 11/09/2026 — coluna
 * inexistente no `select` volta como `42703`, "column <tabela>.<coluna> does
 * not exist".
 *
 * So responde true se o erro CITA uma das colunas: outro erro qualquer nao pode
 * ser confundido com a migration atrasada.
 */
export function isColunaAusente(error: unknown, colunas: string[]): boolean {
  if (!error || typeof error !== "object") return false
  const e = error as { code?: string; message?: string; details?: string; hint?: string }
  const texto = `${e.message ?? ""} ${e.details ?? ""} ${e.hint ?? ""}`
  if (!colunas.some((c) => new RegExp(`\\b${c}\\b`).test(texto))) return false
  return e.code === "42703" || e.code === "PGRST204" || /does not exist|could not find/i.test(texto)
}

/** Linhas por pagina — igual ao teto do servidor, para nunca pedir mais do que ele entrega. */
export const TAMANHO_PAGINA = 1000

/** Teto de seguranca: 20 paginas = 20 mil linhas. Acima disso o periodo e grande demais. */
const MAX_PAGINAS = 20

/**
 * Quantas paginas pedir DE UMA VEZ depois da primeira.
 *
 * A leitura era estritamente serial: pedia a pagina 1, esperava, pedia a 2. Na
 * Braga (7.368 parcelas em aberto = 8 paginas) isso vira 8 idas e voltas
 * enfileiradas. O banco responde cada uma em milissegundos — o custo e a
 * viagem, ~230 ms medidos. Oito viagens em fila = ~2 s de tela parada por uma
 * conta que o banco faz em 40 ms.
 *
 * 4 e um meio-termo deliberado: derruba 8 viagens para 3 ondas sem abrir mao
 * do caso comum (unidade pequena continua fazendo UMA requisicao) e sem
 * disparar dezenas de conexoes simultaneas contra um banco que e compartilhado
 * com outro produto.
 */
const PAGINAS_EM_PARALELO = 4

export interface ResultadoPaginado<T> {
  linhas: T[]
  /** True quando o teto de `MAX_PAGINAS` foi atingido e ainda havia mais dados. */
  truncado: boolean
  error: unknown
}

/**
 * Forma minima da resposta do supabase-js que este helper precisa.
 *
 * Tipar assim — em vez de importar `PostgrestFilterBuilder` — mantem o helper
 * independente da assinatura generica do postgrest-js, que muda entre versoes
 * e exige que o tipo da linha seja `Record<string, unknown>`.
 */
export interface RespostaPagina<T> {
  data: T[] | null
  error: unknown
}

/**
 * Busca todas as paginas de uma consulta, respeitando o teto de linhas do
 * servidor.
 *
 * @param montarQuery recebe o intervalo e devolve a query ja com `.range()`
 *   aplicado. Recebe a funcao (e nao a query pronta) porque um
 *   `PostgrestFilterBuilder` e "thenable": uma vez aguardado, nao pode ser
 *   reaproveitado para a proxima pagina.
 */
export async function fetchAllPages<T>(
  montarQuery: (de: number, ate: number) => PromiseLike<RespostaPagina<T>>,
): Promise<ResultadoPaginado<T>> {
  const linhas: T[] = []

  // A primeira pagina vai SOZINHA, de proposito: a grande maioria das unidades
  // cabe nela, e nesses casos a leitura continua sendo uma unica requisicao —
  // sem pedir paginas vazias "por precaucao".
  const primeira = await montarQuery(0, TAMANHO_PAGINA - 1)
  if (primeira.error) return { linhas, truncado: false, error: primeira.error }

  const inicial = (primeira.data ?? []) as T[]
  linhas.push(...inicial)
  if (inicial.length < TAMANHO_PAGINA) return { linhas, truncado: false, error: null }

  // Ainda ha mais. Daqui em diante as paginas vao em ONDAS paralelas: a viagem
  // ate o servidor custa mais que a consulta em si, entao esperar uma de cada
  // vez e o desperdicio.
  for (let pagina = 1; pagina < MAX_PAGINAS; pagina += PAGINAS_EM_PARALELO) {
    const onda: PromiseLike<RespostaPagina<T>>[] = []
    for (let i = 0; i < PAGINAS_EM_PARALELO && pagina + i < MAX_PAGINAS; i++) {
      const de = (pagina + i) * TAMANHO_PAGINA
      onda.push(montarQuery(de, de + TAMANHO_PAGINA - 1))
    }

    // `Promise.all` preserva a ordem do pedido, entao as linhas continuam
    // saindo na mesma ordem da versao serial. Isso importa: quem consome
    // depende da ordenacao da consulta (vencimento, data), nao de reordenar
    // depois.
    const respostas = await Promise.all(onda)

    for (const r of respostas) {
      // O erro so conta se as paginas ANTERIORES vieram cheias. Uma onda pede
      // paginas que podem estar depois do fim dos dados; se a lista ja acabou
      // numa pagina anterior, o que veio depois e ruido — falhar por causa dele
      // transformaria uma leitura completa em erro na tela.
      if (r.error) return { linhas, truncado: false, error: r.error }

      const lote = (r.data ?? []) as T[]
      linhas.push(...lote)
      if (lote.length < TAMANHO_PAGINA) return { linhas, truncado: false, error: null }
    }
  }

  return { linhas, truncado: true, error: null }
}

/**
 * Traduz um erro do banco para uma frase que o usuário consegue usar.
 *
 * 🚨 ESTA FUNÇÃO DEVOLVIA O ERRO CRU — e ele chegava à tela. Em 10/08/2026, a
 * Braga ESC (61.406 parcelas) abria o módulo financeiro assim:
 *
 *     Não foi possível carregar os lançamentos do período
 *     canceling statement due to statement timeout
 *
 * A segunda linha é a mensagem do PostgreSQL, em inglês, para um administrador
 * de ESC. Não diz nada a quem lê, expõe o interior do sistema e não indica ação
 * nenhuma — exatamente o que a regra `user-facing-communication.md` proíbe
 * (a máxima: "o técnico nunca aparece ao usuário").
 *
 * Agora o detalhe técnico vai para o console, onde nós o lemos, e a tela recebe
 * o EFEITO e o QUE FAZER. Quando não dá para traduzir, usa o texto genérico de
 * quem chamou — nunca o texto do banco.
 */
export function mensagemDeErro(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback

  const e = error as { message?: string; details?: string; hint?: string; code?: string }

  // O original fica registrado para nós. Nunca para o usuário.
  console.error("[financeiro] falha ao ler dados", error)

  const texto = `${e.message ?? ""} ${e.details ?? ""} ${e.code ?? ""}`.toLowerCase()

  // Volume acima do que o banco entrega dentro do tempo. É o caso da Braga, e é
  // o único em que o usuário tem uma saída imediata: reduzir o escopo.
  if (texto.includes("statement timeout") || texto.includes("57014")) {
    return (
      "Esta unidade tem parcelas demais para carregar de uma vez. " +
      "Escolha um mês mais recente ou uma unidade específica no topo. " +
      "Se continuar assim, avise o suporte — já estamos com este caso mapeado."
    )
  }

  // Sem permissão: quem resolve é o administrador, não quem está na tela.
  if (texto.includes("permission denied") || texto.includes("42501") || texto.includes("jwt")) {
    return "Seu acesso não permite ver os dados financeiros desta unidade. Fale com o administrador da ESC."
  }

  return fallback
}
